import type React from 'react';
import { unit } from '@ant-design/cssinjs';
import type { CSSInterpolation } from '@ant-design/cssinjs';
import { FastColor } from '@ant-design/fast-color';

import { resetComponent } from '../../style';
import type { FullToken, GenStyleFn, GetDefaultToken } from '../../theme/internal';
import { genStyleHooks, mergeToken } from '../../theme/internal';

export interface ComponentToken {
  /**
   * @desc 默认背景色
   * @descEN Default background color
   */
  defaultBg: string;
  /**
   * @desc 默认文字颜色
   * @descEN Default text color
   */
  defaultColor: string;
}

export interface TagToken extends FullToken<'Tag'> {
  tagFontSize: number;
  tagLineHeight: React.CSSProperties['lineHeight'];
  tagIconSize: number | string;
  tagPaddingHorizontal: number;
  tagBorderlessBg: string;
  // Size variants
  tagMiddlePaddingHorizontal: number;
  tagMiddlePaddingVertical: number;
  tagMiddleBorderRadius: number;
  tagLargePaddingHorizontal: number;
  tagLargePaddingVertical: number;
  tagLargeBorderRadius: number;
}

// ============================== Styles ==============================

const genBaseStyle = (token: TagToken): CSSInterpolation => {
  const {
    paddingXXS,
    lineWidth,
    componentCls,
    calc,
    tagMiddlePaddingHorizontal,
    tagMiddlePaddingVertical,
    tagMiddleBorderRadius,
    tagLargePaddingHorizontal,
    tagLargePaddingVertical,
    tagLargeBorderRadius,
  } = token;
  const iconMarginInline = calc(paddingXXS).sub(lineWidth).equal();

  // Large size padding
  const largePaddingInline = calc(tagLargePaddingHorizontal).sub(lineWidth).equal();
  const largePaddingBlock = calc(tagLargePaddingVertical).sub(lineWidth).equal();

  return {
    // Result
    [componentCls]: {
      ...resetComponent(token),
      display: 'inline-block',
      height: 'auto',
      // https://github.com/ant-design/ant-design/pull/47504
      marginInlineEnd: token.marginXS,
      // Default to middle size
      paddingInline: tagMiddlePaddingHorizontal,
      paddingBlock: tagMiddlePaddingVertical,
      fontSize: 12,
      lineHeight: token.tagLineHeight,
      whiteSpace: 'nowrap',
      background: token.defaultBg,
      // border: `${unit(token.lineWidth)} ${token.lineType} ${token.colorBorder}`,
      borderRadius: tagMiddleBorderRadius,
      opacity: 1,
      transition: `all ${token.motionDurationMid}`,
      textAlign: 'start',
      position: 'relative',

      // Size: middle (default)
      [`&${componentCls}-middle`]: {
        paddingInline: tagMiddlePaddingHorizontal,
        paddingBlock: tagMiddlePaddingVertical,
        borderRadius: tagMiddleBorderRadius,
      },

      // Size: large
      [`&${componentCls}-large`]: {
        paddingInline: largePaddingInline,
        paddingBlock: largePaddingBlock,
        borderRadius: tagLargeBorderRadius,
        fontSize: 14,
      },

      // RTL
      [`&${componentCls}-rtl`]: {
        direction: 'rtl',
      },

      '&, a, a:hover': {
        color: token.defaultColor,
      },

      [`${componentCls}-close-icon`]: {
        marginInlineStart: iconMarginInline,
        fontSize: token.tagIconSize,
        color: token.colorTextDescription,
        cursor: 'pointer',
        transition: `all ${token.motionDurationMid}`,

        '&:hover': {
          color: token.colorTextHeading,
        },
      },

      [`&${componentCls}-has-color`]: {
        borderColor: 'transparent',

        [`&, a, a:hover, ${token.iconCls}-close, ${token.iconCls}-close:hover`]: {
          color: token.colorTextLightSolid,
        },
      },

      '&-checkable': {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        cursor: 'pointer',

        [`&:not(${componentCls}-checkable-checked):hover`]: {
          color: token.colorPrimary,
          backgroundColor: token.colorFillSecondary,
        },

        '&:active, &-checked': {
          color: token.colorTextLightSolid,
        },

        '&-checked': {
          backgroundColor: token.colorPrimary,
          '&:hover': {
            backgroundColor: token.colorPrimaryHover,
          },
        },

        '&:active': {
          backgroundColor: token.colorPrimaryActive,
        },
      },

      '&-hidden': {
        display: 'none',
      },

      // To ensure that a space will be placed between character and `Icon`.
      [`> ${token.iconCls} + span, > span + ${token.iconCls}`]: {
        marginInlineStart: tagMiddlePaddingHorizontal,
      },
    },
    [`${componentCls}-borderless`]: {
      borderColor: 'transparent',
      background: token.tagBorderlessBg,
    },
  };
};

// ============================== Export ==============================
export const prepareToken: (token: Parameters<GenStyleFn<'Tag'>>[0]) => TagToken = (token) => {
  const { lineWidth, fontSizeIcon, calc } = token;
  const tagFontSize = token.fontSizeSM;
  const tagToken = mergeToken<TagToken>(token, {
    tagFontSize,
    tagLineHeight: unit(calc(token.lineHeightSM).mul(tagFontSize).equal()),
    tagIconSize: calc(fontSizeIcon).sub(calc(lineWidth).mul(2)).equal(), // Tag icon is much smaller
    tagPaddingHorizontal: 8, // Fixed padding.
    tagBorderlessBg: token.defaultBg,
    tagMiddlePaddingHorizontal: 12,
    tagMiddlePaddingVertical: 8,
    tagMiddleBorderRadius: 1000,
    tagLargePaddingHorizontal: 12,
    tagLargePaddingVertical: 8,
    tagLargeBorderRadius: 100,
  });
  return tagToken;
};

export const prepareComponentToken: GetDefaultToken<'Tag'> = (token) => ({
  defaultBg: new FastColor(token.colorFillQuaternary)
    .onBackground(token.colorBgContainer)
    .toHexString(),
  defaultColor: token.colorText,
});

export default genStyleHooks<'Tag'>(
  'Tag',
  (token) => {
    const tagToken = prepareToken(token);
    return genBaseStyle(tagToken);
  },
  prepareComponentToken,
);
